<!-- The Subscriptions dialog (Seán's spec, 2026-10-23): one dialog for #subscriptions (view and
     change), #subscribe (all on as it opens), #unsubscribe (all off) and an email's link by its
     token (done as it opens, as the others). The state, the entry action
     and the hash: useSubscriptionsDialog.js. The lines under the title follow what's saved; each
     switch saves at once, showing a spinner till it's saved, and Close waits for every save
     (nor do ✕, Escape or a click outside close it meanwhile). A failed save is put back as it
     was, with Retry. -->
<template>
  <AppModalDialog :hash="DIALOG_HASH" :is-held-open="saves.isPending.value">
    <!-- Profile at the top left, level with ✕ (Seán, 2026-10-23): a breadcrumb back up to the
         dialog this is a part of, ‹ their picture and "Profile", one button; secondary, so plain -->
    <template #header-left>
      <!-- signed out too (an email's link): #profile signs them in first, then opens -->
      <button type="button" class="profile" @click="openProfile">
        <svg class="chevron" viewBox="0 0 10 16" aria-hidden="true"><path d="M8 2L2 8l6 6" /></svg>
        <!-- their own picture, as the page header's (ButtonUser.vue); inert: the avatar is an
             upload button of its own, not here -->
        <span class="picture" inert>
          <AppModalProfileAvatar v-if="isSignedIn" :user />
          <!-- signed out: the header's generic person (ButtonUser.vue) -->
          <svg v-else class="anonymous" viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="12" cy="12" r="11" fill="none" stroke="currentColor" stroke-width="1.5" />
            <circle cx="12" cy="9.5" r="3.5" fill="currentColor" />
            <path d="M5.5 19.5c1.3-3 3.8-4.5 6.5-4.5s5.2 1.5 6.5 4.5" fill="currentColor" />
          </svg>
        </span>
        Profile
      </button>
    </template>
    <AppFormWrapper>
      <h2 class="title">Subscriptions</h2>
      <p v-if="isLinkBroken" class="error" role="alert">
        This link didn't work. Please sign in to change your subscriptions, or contact us.
      </p>
      <AppModalProfileSettingsSwitches v-if="saves.confirmed.value" :rows @toggle="toggle" />
      <!-- under the switches (Seán, 2026-10-23: it follows them); every variant in one place, the
           one that applies shown: no jump as it changes -->
      <div v-if="!isLinkBroken" class="lines">
        <p
          v-for="(text, key) in STATUS"
          :key
          class="status"
          :class="{isShown: key === statusKey}"
          :aria-hidden="key === statusKey ? undefined : 'true'">
          {{ text }}
        </p>
      </div>
      <p v-if="saves.isFailed.value" class="error" role="alert">
        We couldn't save your change.
        <button type="button" class="retry" @click="saves.retry">Retry</button>
      </p>
      <span class="srOnly" aria-live="polite">{{ saves.isPending.value ? "Saving…" : "" }}</span>
    </AppFormWrapper>
    <template #footer>
      <ButtonBespoke
        ref="closeRef"
        outstanding
        :disabled="saves.isPending.value"
        @click="close">
        <AppSpinner v-if="saves.isPending.value" class="closeSpinner" />
        Close
      </ButtonBespoke>
    </template>
  </AppModalDialog>
</template>

<script setup>
import {computed, nextTick, ref, watch} from "vue";
import {useRoute, useRouter} from "vue-router";
import AppModalDialog from "@/components/App/Modal/Dialog/index";
import AppFormWrapper from "@/components/App/Form/Wrapper";
import AppModalProfileAvatar from "@/components/App/Modal/Profile/Avatar";
import AppModalProfileSettingsSwitches from "@/components/App/Modal/Profile/SettingsSwitches";
import AppSpinner from "@/components/App/Spinner";
import ButtonBespoke from "@/components/ButtonBespoke";
import useCleanHash from "@/composables/useCleanHash";
import useUserSafe from "@/composables/useUserSafe";
import {LINK_HASH, LIST_KEYS} from "@/composables/useMailingListLink";
import {LIST_LABELS} from "@/composables/useMailingLists";
import {useSubscriptionsDialog} from "@/composables/useSubscriptionsDialog";

const DIALOG_HASH = new RegExp(`^${LINK_HASH.source.slice(2)}`); // the dialog's: no "#"
// what the saved lists amount to (Seán, 2026-10-23)
const STATUS = {
  loading: "Loading your subscriptions…",
  none: "You'll only receive emails about VIZBI events you've registered for.",
  some: "You'll receive email updates about selected VIZBI events.",
  all: "You'll receive email updates about all VIZBI events.",
};

function getStatusKey(lists) {
  if (!lists) return "loading";
  if (!lists.length) return "none";
  return lists.length === LIST_KEYS.length ? "all" : "some";
}

const route = useRoute();
const router = useRouter();
const {user} = useUserSafe();
const {entry, saves, isLinkBroken, isSignedIn, toggle} =
  useSubscriptionsDialog();
const statusKey = computed(() => getStatusKey(saves.confirmed.value));
const rows = computed(() => LIST_KEYS.map((key) => ({
  key,
  label: LIST_LABELS[key],
  value: saves.desired.value.includes(key),
  isPending: saves.pendingKeys.value.includes(key),
})));

// the first focus on Close, the default action (Seán, 2026-10-23), once per opening; while a save
// is on its way (#subscribe as it opens) Close can't take it, so it waits till it's enabled
const closeRef = ref(null);
let focusedEntry = null;

// once the dialog is open: opening it puts the focus on its first control
function focusCloseWhenOpen(framesLeft = 30) {
  const button = closeRef.value?.$el;
  if (button?.closest("dialog")?.open) return button.focus();
  if (framesLeft) requestAnimationFrame(() => focusCloseWhenOpen(framesLeft - 1));
}

// ready: the lists loaded, the entry action (if any) begun, nothing on its way
const isReady = computed(() => {
  const {token, isActionDone} = entry.value || {};
  return Boolean(saves.confirmed.value && (token || isActionDone) && !saves.isPending.value);
});

watch([entry, isReady], async ([opened, ready]) => {
  if (!opened || !ready || opened === focusedEntry) return;
  focusedEntry = opened;
  await nextTick();
  focusCloseWhenOpen();
}, {immediate: true});

function close() {
  if (saves.isPending.value) return;
  router.replace({path: route.path, hash: ""});
}

function openProfile() {
  if (saves.isPending.value) return;
  router.push({hash: "#profile"});
}

useCleanHash(); // removes the '/' vue-router puts before the hash
</script>

<style scoped>
/* the spacing as seen, not as boxes (Seán, 2026-10-23): ~16px from Profile's picture to the
   title's capitals, ~22px from the title to the switches, the line 12px under them; to Close,
   the dialogs' own gap (Dialog/Footer.vue) */
.title {
  margin: 4px 0 17px;
}
/* the line in one cell: as tall as its longest variant, whichever is shown */
.lines {
  display: grid;
  margin-top: 12px;
}
.lines p {
  grid-area: 1 / 1;
  margin: 0;
  visibility: hidden;
}
.lines p.isShown {
  visibility: visible;
}
/* the switches (Seán, 2026-10-23): the shared rows (SettingsSwitches.vue), roomier here than in
   #profile; a list switched off is still plainly a choice, its label as dark as the others */
:deep(.settingsSection) {
  margin: 0;
}
:deep(.settingsSection .row) {
  min-height: 44px;
  padding: 0 12px;
}
:deep(.settingsSection .label),
:deep(.settingsSection .isOff .label) {
  font-size: 15px;
  font-weight: 400;
  color: #222 !important;
}
:deep(.settingsSection .switch) {
  --pill-width: 40px;
  --pill-height: 23px;
  --circle-size: 17px;
}
/* a failure: the signal red (DESIGN.md § 5; FORMS.md § 10's) */
.error {
  margin: 10px 0 0;
  font-size: 14px;
  color: #c62828 !important;
}
.retry {
  margin: 0 0 0 0.4em;
  padding: 0;
  border: none;
  border-radius: 0;
  box-shadow: none;
  background: none;
  color: inherit !important;
  font: inherit;
  font-weight: 700;
  text-decoration: underline;
  text-underline-offset: 0.15em;
  cursor: pointer;
}
/* the way to the rest, Profile → Settings (global.css's button card reset): drawn as low as
   ✕ is (Dialog/Header.vue's .close), so the two are level; the icon in line with the content's
   left edge, the padding round it the target */
.profile {
  position: relative;
  top: calc(var(--dialog-padding-x, 24px) - 13.7px); /* its icon's centre on the cross's */
  display: flex;
  align-items: center;
  gap: 8px;
  height: 36px;
  margin: 0 0 0 -6px;
  padding: 0 6px;
  border: none;
  border-radius: 8px;
  box-shadow: none;
  background: none;
  /* the same in dark mode, the dialog's card being light: the lightest grey readable on it
     (#666 on #e4e4e4: 4.5:1, as Sign In's note), below the title (Seán, 2026-10-23) */
  color: #666;
  font: inherit;
  font-size: 15px;
  font-weight: 400;
  cursor: pointer;
}
.profile:hover {
  color: #222;
  background: rgba(0, 0, 0, 0.05);
}
.chevron {
  width: 7px;
  height: 12px;
  flex: 0 0 auto;
  fill: none;
  stroke: currentColor;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
}
.picture {
  display: grid;
  flex: 0 0 auto;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  overflow: hidden;
}
.anonymous {
  width: 100%;
  height: 100%;
}
.picture :deep(.avatar) {
  box-shadow: none;
}
.closeSpinner {
  margin-right: 0.5em;
}
.srOnly {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}
</style>
